import { Box, Typography, Chip, Stack, Tooltip } from '@mui/material';
import {
  InfrastructureType,
  InfrastructureRelationship,
  InfrastructureRequirement,
  infrastructureTypes,
} from '@nohara-dxdev/mt-common';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface GroupedRequirement {
  infrastructureType: InfrastructureType;
  requirements: InfrastructureRequirement[];
}

interface RequiredInfrastructureDisplayProps {
  infrastructureRequirements?: InfrastructureRequirement[];
}

const infrastructureTypeOrderIndex = new Map<InfrastructureType, number>(
  infrastructureTypes.map((type, index) => [type, index]),
);

const sortGroupedRequirementsByTabOrder = (groups: GroupedRequirement[]): GroupedRequirement[] => {
  return [...groups].sort((a, b) => {
    const ia = infrastructureTypeOrderIndex.get(a.infrastructureType) ?? Number.POSITIVE_INFINITY;
    const ib = infrastructureTypeOrderIndex.get(b.infrastructureType) ?? Number.POSITIVE_INFINITY;
    return ia - ib;
  });
};

const groupByInfrastructureType = (
  requirements: InfrastructureRequirement[],
): GroupedRequirement[] => {
  const grouped = new Map<InfrastructureType, InfrastructureRequirement[]>();

  for (const req of requirements) {
    const existing = grouped.get(req.infrastructureType) ?? [];
    existing.push(req);
    grouped.set(req.infrastructureType, existing);
  }

  return Array.from(grouped.entries()).map(([infrastructureType, requirements]) => ({
    infrastructureType,
    requirements,
  }));
};

export const RequiredInfrastructureDisplay = ({
  infrastructureRequirements = [],
}: RequiredInfrastructureDisplayProps) => {
  const { t } = useTranslation();

  const groupedRequirements = useMemo(() => {
    const grouped = groupByInfrastructureType(infrastructureRequirements);
    return sortGroupedRequirementsByTabOrder(grouped);
  }, [infrastructureRequirements]);

  const formatRelationship = (relationship?: InfrastructureRelationship | 'both'): string => {
    if (!relationship) return '';
    if (relationship === 'both') {
      return `${t('infrastructure.relationshipDirection.parent')} | ${t('infrastructure.relationshipDirection.child')}`;
    }
    return t(`infrastructure.relationshipDirection.${relationship}`);
  };

  const getInfrastructureTypeLabel = (type: InfrastructureType): string => {
    const key = `infrastructure.type.${type}`;
    return t(key);
  };

  if (groupedRequirements.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {t('itemTemplates.detail.requiredInfrastructure')}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {groupedRequirements.map((group) => (
          <Box key={group.infrastructureType}>
            {group.requirements.map((req, index) => {
              const isMain = req.relationshipLevel === 'main';
              const tooltipContent = (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('infrastructure.tooltip.type')}:{' '}
                    {t(`infrastructure.level.${req.relationshipLevel}`)}
                  </Typography>
                  {req.infrastructureRelationship && (
                    <Typography variant="body2">
                      {t('infrastructure.tooltip.required')}:{' '}
                      {formatRelationship(req.infrastructureRelationship)}
                    </Typography>
                  )}
                </Box>
              );

              return (
                <Tooltip key={`${group.infrastructureType}-${index}`} title={tooltipContent} arrow>
                  <Chip
                    label={getInfrastructureTypeLabel(group.infrastructureType)}
                    size="small"
                    sx={{
                      backgroundColor: isMain ? '#d32f2f' : '#ffcdd2',
                      color: isMain ? '#fff' : '#c62828',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: isMain ? '#c62828' : '#ef9a9a',
                      },
                      mb: 0.5,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
